"use client";

import { RegisterSchema, RegisterType } from "@/lib/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import { startTransition, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { signUpWorker } from "@/services/auth";
import { toast } from "sonner";
import { FEDERAL_WORKER_USERTYPE } from "@/lib/constants";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";
import Link from "next/link";

export default function WorkerRegister() {
  const [registerLoading, setRegisterLoading] = useState(false);
  const methods = useForm<RegisterType>({ resolver: zodResolver(RegisterSchema) });
  const {
    handleSubmit,
    formState: { errors },
    register,
    control,
  } = methods;

  const ippis = useWatch({ control, name: "ippis" });
  const bvn = useWatch({ control, name: "bvn" });
  const accountNumber = useWatch({ control, name: "accountNumber" });

  const isFormFilled = Boolean(ippis && bvn && accountNumber);
  const router = useRouter();
  const onSubmit = async (data: RegisterType) => {
    setRegisterLoading(true);
    try {
      const res = await signUpWorker({
        ippisNumber: data.ippis,
        bvn: data.bvn,
        accountNumber: data.accountNumber,
      });
      if (res.error) {
        setRegisterLoading(false);
        // todo: placeholder error message, replace with actual error message
        toast.error("An error occured");
        return;
      }
      if (res.email) {
        setRegisterLoading(false);
        toast.success("Account created successfully. Please set your password to continue");
      }
      if (!res.email) {
        setRegisterLoading(false);
        toast.error("Email not found with your WACS details");
        return;
      }
      router.push(`/register/${FEDERAL_WORKER_USERTYPE}/password?identifier=${encodeURIComponent(data.ippis)}`);
    } catch (error) {
      setRegisterLoading(false);
      console.log(error);
      // todo: placeholder error message, replace with actual error message
      toast.error("An error occured");
      return;
    }
  };
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    startTransition(() => {
      setMounted(true);
    });
  }, []);
  return (
    <div className=''>
      <div
        className={cn(
          "text-link-primary mx-auto mb-2 flex w-fit items-center gap-1 text-4xl font-medium md:text-5xl lg:text-6xl 2xl:mt-[5%] 2xl:mb-5"
        )}
      >
        <Image
          src={mounted && theme === "dark" ? "/assets/icons/logo-dark.svg" : "/assets/icons/logo-light.svg"}
          alt='Onyx Logo'
          width={82}
          height={82}
          className='mr-2 inline-flex size-12 md:size-14 lg:size-16'
        />
        Onyx <span className='text-link-primary'>.</span>
      </div>
      <h1 className='text-text-title text-center text-2xl leading-[120%] font-semibold md:text-4xl'>
        Let&apos;s make spending easier
        <br />
        <span className='text-text-title/70 text-xl md:text-3xl'>
          Introduce yourself to Onyx<span className='text-link-primary'>.</span>
        </span>
      </h1>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className='mx-auto mt-5 flex w-full flex-col gap-4.5 md:w-5/6'>
          <div className='flex flex-col items-start gap-2.5'>
            <Label className='justify-center'>What&apos;s your IPPIS?</Label>
            <Input
              aria-invalid={errors.ippis ? "true" : "false"}
              type='number'
              className='px-6'
              placeholder='Write your IPPIS number'
              {...register("ippis")}
            />
            {errors.ippis && (
              <p aria-invalid='true' className='text-text-destructive text-xs'>
                {errors.ippis.message}
              </p>
            )}
          </div>
          <div className='flex flex-col items-start gap-2.5'>
            <Label className='justify-center'>What&apos;s your BVN?</Label>
            <Input
              aria-invalid={errors.bvn ? "true" : "false"}
              type='number'
              className='px-6'
              placeholder='Write your BVN'
              {...register("bvn")}
            />
            {errors.bvn && (
              <p aria-invalid='true' className='text-text-destructive text-xs'>
                {errors.bvn.message}
              </p>
            )}
          </div>
          <div className='flex flex-col items-start gap-2.5'>
            <Label className='justify-center'>What&apos;s your Bank Account Number?</Label>
            <Input
              aria-invalid={errors.accountNumber ? "true" : "false"}
              type='number'
              className='px-6'
              placeholder='Write your Bank Account Number'
              {...register("accountNumber")}
            />
            {errors.accountNumber && (
              <p aria-invalid='true' className='text-text-destructive text-xs'>
                {errors.accountNumber.message}
              </p>
            )}
          </div>

          <Button
            type='submit'
            loading={registerLoading}
            disabled={!isFormFilled || registerLoading}
            className='mx-auto mt-5 w-3/4 py-6 text-xs lg:mt-0'
          >
            Continue
          </Button>
        </form>
      </FormProvider>
      <p className='text-text-title/70 mt-5 text-center text-sm'>
        Already have an account?{" "}
        <Link href={`/login/${FEDERAL_WORKER_USERTYPE}`} className='text-link-primary'>
          Sign in
        </Link>
      </p>
    </div>
  );
}
